import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../contexts/AuthContext";
import WebNotification from '../../../common/WebNotification';
import PushNotificationSettings from '../../../common/PushNotificationSettings';

export default function UserAccount() {
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userName, setUserName] = useState("");
  const { user, logout } = useAuth();
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        (trigger.current && trigger.current.contains(target))
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  // prevent body scroll when dropdown is open (mobile)
  useEffect(() => {
    if (dropdownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [dropdownOpen]);

  // Set user data when user is available
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setUserName(user.userName || "");
    }
  }, [user]);

  const fixedWrap = () => {
    setDropdownOpen(false);
  };

  const handleChangePassword = () => {
    navigate("/SetPassword", { state: { user, flow: "changePassword" } });
  };

  return (
    <div className="container">
      <div className="flex justify-end items-center h-[70px]">
        <div className="relative inline-block flex items-center px-4">
          <WebNotification userId={user?.id} />
        </div>
        <div className="relative inline-block flex items-center gap-5">
          {/* avatar trigger */}
          <button
            ref={trigger}
            onClick={() => setDropdownOpen((s) => !s)}
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            className="w-9 h-10 rounded-xl overflow-hidden p-0 border-0 bg-transparent cursor-pointer"
            title="Account menu"
          >
            <img className="w-9 h-10 rounded-xl object-cover" src={`images/nps-logo.ico`} alt="account" />
          </button>

          <div
            ref={dropdown}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setDropdownOpen(false)}
            className={`absolute right-0 top-full mt-2 z-40 ${dropdownOpen ? "block" : "hidden"} w-[310px] max-w-[95vw] sm:w-[310px]`}
          >
            <div className={`fixed inset-0 bg-black/40 z-30 ${dropdownOpen ? "block" : "hidden"}`} onClick={() => fixedWrap()}></div>
            <div className="absolute z-40 right-0 -top-[2px]"><svg xmlns="http://www.w3.org/2000/svg" width="43" height="33" viewBox="0 0 43 33" fill="none">
              <g filter="url(#filter0_d_132_1543)">
                <path d="M21.5 5L37.5215 27.5H5.47853L21.5 5Z" fill="white" />
              </g>
              <defs>
                <filter id="filter0_d_132_1543" x="0.478516" y="0" width="42.043" height="32.5" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="2.5" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_132_1543" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_132_1543" result="shape" />
                </filter>
              </defs>
            </svg></div>
            <div className="mt-4 px-4 relative overflow-hidden shadow-lg inset-shadow-md z-50 rounded-lg bg-white">

              <div className="h-8 w-full flex"></div>
              <div className="flex items-center gap-3 py-3">
                <div className="relative w-10 h-10 rounded overflow-hidden">
                  <img
                    src={`images/nps-logo.ico`}
                    alt="account"
                    className="w-10 h-10 rounded object-cover"
                  />
                  {/* <span className="absolute -right-0.5 -top-0.5 block h-3.5 w-3.5 rounded-full border-2 border-white bg-green dark:border-dark"></span> */}
                </div>
                <div>
                  <p className="text-sm font-semibold text-dark">
                    {firstName} {lastName}
                  </p>
                  <p className="text-sm text-body-color">
                    {userName}
                  </p>
                </div>
              </div>
              <div>
                <ul className="mt-4">
                  <li className="flex items-center mb-5 cursor-pointer" onClick={handleChangePassword}>
                    <div className="flex rounded-full w-10 h-10 bg-[#F3F7F8] items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <g clip-path="url(#clip0_132_1557)">
                          <path d="M14.263 7.94729H14.9648C15.1509 7.94729 15.3294 8.02122 15.461 8.15283C15.5926 8.28443 15.6666 8.46292 15.6666 8.64904V15.6666C15.6666 15.8527 15.5926 16.0312 15.461 16.1628C15.3294 16.2944 15.1509 16.3683 14.9648 16.3683H3.73673C3.55061 16.3683 3.37212 16.2944 3.24051 16.1628C3.10891 16.0312 3.03497 15.8527 3.03497 15.6666V8.64904C3.03497 8.46292 3.10891 8.28443 3.24051 8.15283C3.37212 8.02122 3.55061 7.94729 3.73673 7.94729H4.43848V7.24553C4.43848 6.60044 4.56554 5.96167 4.81241 5.36568C5.05927 4.7697 5.42111 4.22817 5.87726 3.77203C6.3334 3.31588 6.87493 2.95404 7.47091 2.70718C8.0669 2.46031 8.70567 2.33325 9.35076 2.33325C9.99585 2.33325 10.6346 2.46031 11.2306 2.70718C11.8266 2.95404 12.3681 3.31588 12.8243 3.77203C13.2804 4.22817 13.6423 4.7697 13.8891 5.36568C14.136 5.96167 14.263 6.60044 14.263 7.24553V7.94729ZM4.43848 9.3508V14.9648H14.263V9.3508H4.43848ZM8.64901 10.7543H10.0525V13.5613H8.64901V10.7543ZM12.8595 7.94729V7.24553C12.8595 6.31495 12.4899 5.42248 11.8318 4.76446C11.1738 4.10643 10.2813 3.73676 9.35076 3.73676C8.42018 3.73676 7.52771 4.10643 6.86969 4.76446C6.21166 5.42248 5.84199 6.31495 5.84199 7.24553V7.94729H12.8595Z" fill="#545764" />
                        </g>
                        <defs>
                          <clipPath id="clip0_132_1557">
                            <rect width="16.8421" height="16.8421" fill="white" transform="translate(0.929688 0.929688)" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <div className="flex grow mx-4 text-sm font-medium text-[#363948] hover:text-blue-600">
                      Change Password
                    </div>
                  </li>
                  <li className="flex items-center mb-5 cursor-pointer" onClick={logout}>
                    <div className="flex rounded-full w-10 h-10 bg-[#F3F7F8] items-center justify-center">
                      <svg
                        width={18}
                        height={18}
                        viewBox="0 0 18 18"
                        className="fill-current"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.75 3C3.55109 3 3.36032 3.07902 3.21967 3.21967C3.07902 3.36032 3 3.55109 3 3.75V14.25C3 14.4489 3.07902 14.6397 3.21967 14.7803C3.36032 14.921 3.55109 15 3.75 15H6.75C7.16421 15 7.5 15.3358 7.5 15.75C7.5 16.1642 7.16421 16.5 6.75 16.5H3.75C3.15326 16.5 2.58097 16.2629 2.15901 15.841C1.73705 15.419 1.5 14.8467 1.5 14.25V3.75C1.5 3.15326 1.73705 2.58097 2.15901 2.15901C2.58097 1.73705 3.15326 1.5 3.75 1.5H6.75C7.16421 1.5 7.5 1.83579 7.5 2.25C7.5 2.66421 7.16421 3 6.75 3H3.75Z"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11.4697 4.71967C11.7626 4.42678 12.2374 4.42678 12.5303 4.71967L16.2803 8.46967C16.5732 8.76256 16.5732 9.23744 16.2803 9.53033L12.5303 13.2803C12.2374 13.5732 11.7626 13.5732 11.4697 13.2803C11.1768 12.9874 11.1768 12.5126 11.4697 12.2197L14.6893 9L11.4697 5.78033C11.1768 5.48744 11.1768 5.01256 11.4697 4.71967Z"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6 9C6 8.58579 6.33579 8.25 6.75 8.25H15.75C16.1642 8.25 16.5 8.58579 16.5 9C16.5 9.41421 16.1642 9.75 15.75 9.75H6.75C6.33579 9.75 6 9.41421 6 9Z"
                        />
                      </svg>
                    </div>
                    <div className="flex grow mx-4 text-sm font-medium text-[#E74136] hover:text-blue-600">
                      Log out
                    </div>
                  </li>
                </ul>

                {/* Push Notification Settings - separate section */}
                <div className="border-t pt-4 pb-2">
                  <PushNotificationSettings />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}